import { useEffect, useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { z } from 'zod';
import { CheckCircle2Icon, LoaderIcon } from 'lucide-react';
import { AuthError } from '@/components/auth/auth-error';
import { Button } from '@/components/ui/button';
import { EmptyState } from '@/components/empty-state';
import { messageForError } from '@/lib/api-client';
import { authApi } from '@/lib/api/auth';

const searchSchema = z.object({ token: z.string().optional() });

type State = 'idle' | 'verifying' | 'verified' | 'failed';

function VerifyEmailPage() {
  const { token } = Route.useSearch();
  const [state, setState] = useState<State>(token ? 'verifying' : 'idle');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;

    void (async () => {
      try {
        await authApi.verifyEmail({ token });
        if (!cancelled) setState('verified');
      } catch (caught) {
        if (cancelled) return;
        setError(messageForError(caught, 'That confirmation link is no longer valid.'));
        setState('failed');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [token]);

  if (!token) {
    return (
      <EmptyState
        kicker="Email confirmation"
        title="Open the link from your inbox"
        description="Confirmation links are single-use and expire after 24 hours. If yours has lapsed, sign in and request a new one from your profile."
        action={
          <Button asChild variant="secondary">
            <Link to="/login">Go to sign in</Link>
          </Button>
        }
      />
    );
  }

  if (state === 'verifying') {
    return (
      <div className="flex flex-col gap-3">
        <LoaderIcon className="size-5 animate-spin text-ink-subtle" aria-hidden="true" />
        <h1 className="text-title font-semibold tracking-tight text-ink">Confirming your email…</h1>
        <p className="text-data text-ink-muted">This only takes a moment.</p>
      </div>
    );
  }

  if (state === 'verified') {
    return (
      <div className="flex flex-col gap-4">
        <span className="flex size-9 items-center justify-center rounded-md border border-hairline bg-success-soft text-success">
          <CheckCircle2Icon className="size-4" />
        </span>
        <h1 className="text-title font-semibold tracking-tight text-ink">Email confirmed</h1>
        <p className="text-data leading-relaxed text-ink-muted">
          Your address is verified. You can publish content and receive workspace notifications.
        </p>
        <Button asChild className="self-start">
          <Link to="/app">Continue to the dashboard</Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <h1 className="text-title font-semibold tracking-tight text-ink">
        We could not confirm that link
      </h1>
      <AuthError message={error} />
      <Button asChild variant="secondary" className="self-start">
        <Link to="/login">Back to sign in</Link>
      </Button>
    </div>
  );
}

export const Route = createFileRoute('/(auth)/verify-email')({
  validateSearch: searchSchema,
  component: VerifyEmailPage,
});
